import React, { useState } from 'react';
import styles from './StrategyDetail.module.css';
import { strategyAPI } from '../../services/api';
import {
  MegaphoneIcon,
  SpeechBubbleIcon,
  HandshakeIcon,
  GroupIcon,
  BallotIcon,
} from '../common/Icons/ActionIcons';

interface StrategyData {
  _id: string;
  name: string;
  description: string;
  type: string;
  status: string;
  objectives?: { title: string; description?: string; measurable?: string; deadline?: string; completed?: boolean }[];
  targetAudience?: { persona?: any; customDescription?: string; priority?: string }[];
  keyMessages?: { message: string; audience?: string; channel?: string; priority?: string }[];
  tactics?: { name: string; description?: string; channel?: string; status?: string }[];
  channels?: string[];
  timeline?: { startDate?: string; endDate?: string; phases?: { name: string; startDate?: string; endDate?: string; milestones?: string[] }[] };
  budget?: { total?: number; allocated?: number; spent?: number; currency?: string; breakdown?: { category: string; amount: number; description: string }[]; rationale?: string };
  kpis?: { name: string; description?: string; target?: number; current?: number; unit?: string }[];
  risks?: { description: string; impact?: string; probability?: string; mitigation?: string }[];
  stakeholders?: { name: string; role?: string; influence?: string; interest?: string; engagementStrategy?: string }[];
  notes?: string;
  theoreticalBasis?: string;
  stakeholderMapping?: {
    actors?: { name: string; category?: string; position?: string; interest?: string; exchangeSpace?: string }[];
    interestMatrix?: string;
    interestMatrixData?: { stakeholder: string; invloed: string; belang: string; positie: string }[];
  };
  issueAnalysis?: {
    conflictPoints?: string[];
    gains?: string[];
  };
  coreMessage?: string;
  participationLevels?: { stakeholderGroup: string; level: string; reasoning?: string }[];
}

interface StrategyDetailProps {
  strategy: StrategyData;
  onClose: () => void;
  onUpdate?: (updated: StrategyData) => void;
}

const typeLabels: Record<string, string> = {
  communication: 'Communicatie',
  engagement: 'Betrokkenheid',
  crisis: 'Crisis',
  campaign: 'Campagne',
  content: 'Content',
  social_media: 'Social Media',
  pr: 'PR',
  other: 'Anders'
};

const statusLabels: Record<string, string> = {
  draft: 'Concept',
  review: 'Review',
  approved: 'Goedgekeurd',
  active: 'Actief',
  paused: 'Gepauzeerd',
  completed: 'Afgerond',
  archived: 'Gearchiveerd'
};

const statusColors: Record<string, string> = {
  draft: '#999999',
  review: '#FFA500',
  approved: '#4CAF50',
  active: '#1E3A2F',
  paused: '#FF9800',
  completed: '#2196F3',
  archived: '#607D8B'
};

const levelColors: Record<string, string> = {
  informeren: '#9DA6A8',
  raadplegen: '#666D70',
  consulteren: '#666D70',
  adviseren: '#277CEA',
  coproduceren: '#1E3A2F',
  meebeslissen: '#323232'
};

const riskColors: Record<string, { border: string; bg: string; text: string }> = {
  low: { border: '#4CAF50', bg: '#E8F5E9', text: '#2E7D32' },
  medium: { border: '#FF9800', bg: '#FFF3E0', text: '#EF6C00' },
  high: { border: '#F44336', bg: '#FFEBEE', text: '#C62828' },
  critical: { border: '#9C27B0', bg: '#F3E5F5', text: '#6A1B9A' }
};

const participationLadder = [
  { level: 'informeren', description: 'Eenrichtingsverkeer: informatie verstrekken', icon: <MegaphoneIcon size={16} /> },
  { level: 'raadplegen', description: 'Meningen ophalen voor besluitvorming', icon: <SpeechBubbleIcon size={16} /> },
  { level: 'adviseren', description: 'Advies vragen en meewegen', icon: <HandshakeIcon size={16} /> },
  { level: 'coproduceren', description: 'Gezamenlijk ontwikkelen', icon: <GroupIcon size={16} /> },
  { level: 'meebeslissen', description: 'Gedeelde beslissingsbevoegdheid', icon: <BallotIcon size={16} /> }
];

const typeOptions = [
  { value: 'communication', label: 'Communicatiestrategie' },
  { value: 'engagement', label: 'Betrokkenheidsstrategie' },
  { value: 'crisis', label: 'Crisisstrategie' },
  { value: 'campaign', label: 'Campagnestrategie' },
  { value: 'content', label: 'Contentstrategie' },
  { value: 'social_media', label: 'Social Media Strategie' },
  { value: 'pr', label: 'PR Strategie' },
  { value: 'other', label: 'Anders' }
];

const statusOptions = Object.entries(statusLabels).map(([value, label]) => ({ value, label }));

export const StrategyDetail: React.FC<StrategyDetailProps> = ({ strategy: initialStrategy, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'stakeholders' | 'issues'>('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [strategy, setStrategy] = useState<StrategyData>(initialStrategy);
  const [editData, setEditData] = useState<StrategyData>(initialStrategy);

  const startEditing = () => {
    setEditData({ ...strategy });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditData({ ...strategy });
    setEditing(false);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const updated = await strategyAPI.update(strategy._id, editData);
      setStrategy(updated);
      setEditing(false);
      onUpdate?.(updated);
    } catch (err) {
      console.error('Failed to save strategy:', err);
      alert('Opslaan mislukt. Probeer het opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  // Helper to update nested editData
  const updateField = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // ---- Objective helpers ----
  const addObjective = () => {
    const objectives = [...(editData.objectives || []), { title: '', description: '', completed: false }];
    updateField('objectives', objectives);
  };
  const updateObjective = (idx: number, field: string, value: any) => {
    const objectives = [...(editData.objectives || [])];
    objectives[idx] = { ...objectives[idx], [field]: value };
    updateField('objectives', objectives);
  };
  const removeObjective = (idx: number) => {
    updateField('objectives', (editData.objectives || []).filter((_, i) => i !== idx));
  };

  // ---- Key Messages helpers ----
  const addKeyMessage = () => {
    const msgs = [...(editData.keyMessages || []), { message: '', audience: '', channel: '', priority: 'medium' }];
    updateField('keyMessages', msgs);
  };
  const updateKeyMessage = (idx: number, field: string, value: any) => {
    const msgs = [...(editData.keyMessages || [])];
    msgs[idx] = { ...msgs[idx], [field]: value };
    updateField('keyMessages', msgs);
  };
  const removeKeyMessage = (idx: number) => {
    updateField('keyMessages', (editData.keyMessages || []).filter((_, i) => i !== idx));
  };

  // ---- Stakeholder Mapping helpers ----
  const addActor = () => {
    const actors = [...(editData.stakeholderMapping?.actors || []), { name: '', category: '', position: '', interest: '', exchangeSpace: '' }];
    updateField('stakeholderMapping', { ...editData.stakeholderMapping, actors });
  };
  const updateActor = (idx: number, field: string, value: any) => {
    const actors = [...(editData.stakeholderMapping?.actors || [])];
    actors[idx] = { ...actors[idx], [field]: value };
    updateField('stakeholderMapping', { ...editData.stakeholderMapping, actors });
  };
  const removeActor = (idx: number) => {
    const actors = (editData.stakeholderMapping?.actors || []).filter((_, i) => i !== idx);
    updateField('stakeholderMapping', { ...editData.stakeholderMapping, actors });
  };

  // ---- Participation Levels helpers ----
  const addParticipationLevel = () => {
    const levels = [...(editData.participationLevels || []), { stakeholderGroup: '', level: 'informeren', reasoning: '' }];
    updateField('participationLevels', levels);
  };
  const updateParticipationLevel = (idx: number, field: string, value: any) => {
    const levels = [...(editData.participationLevels || [])];
    levels[idx] = { ...levels[idx], [field]: value };
    updateField('participationLevels', levels);
  };
  const removeParticipationLevel = (idx: number) => {
    updateField('participationLevels', (editData.participationLevels || []).filter((_, i) => i !== idx));
  };

  // ---- Issue Analysis helpers ----
  const addConflictPoint = () => {
    const points = [...(editData.issueAnalysis?.conflictPoints || []), ''];
    updateField('issueAnalysis', { ...editData.issueAnalysis, conflictPoints: points });
  };
  const updateConflictPoint = (idx: number, value: string) => {
    const points = [...(editData.issueAnalysis?.conflictPoints || [])];
    points[idx] = value;
    updateField('issueAnalysis', { ...editData.issueAnalysis, conflictPoints: points });
  };
  const removeConflictPoint = (idx: number) => {
    const points = (editData.issueAnalysis?.conflictPoints || []).filter((_, i) => i !== idx);
    updateField('issueAnalysis', { ...editData.issueAnalysis, conflictPoints: points });
  };

  const addGain = () => {
    const gains = [...(editData.issueAnalysis?.gains || []), ''];
    updateField('issueAnalysis', { ...editData.issueAnalysis, gains });
  };
  const updateGain = (idx: number, value: string) => {
    const gains = [...(editData.issueAnalysis?.gains || [])];
    gains[idx] = value;
    updateField('issueAnalysis', { ...editData.issueAnalysis, gains });
  };
  const removeGain = (idx: number) => {
    const gains = (editData.issueAnalysis?.gains || []).filter((_, i) => i !== idx);
    updateField('issueAnalysis', { ...editData.issueAnalysis, gains });
  };

  // ---- Risk helpers ----
  const addRisk = () => {
    const risks = [...(editData.risks || []), { description: '', impact: 'medium', probability: 'medium', mitigation: '' }];
    updateField('risks', risks);
  };
  const updateRisk = (idx: number, field: string, value: any) => {
    const risks = [...(editData.risks || [])];
    risks[idx] = { ...risks[idx], [field]: value };
    updateField('risks', risks);
  };
  const removeRisk = (idx: number) => {
    updateField('risks', (editData.risks || []).filter((_, i) => i !== idx));
  };

  // ---- KPI helpers ----
  const addKpi = () => {
    const kpis = [...(editData.kpis || []), { name: '', description: '', target: 0, current: 0, unit: '' }];
    updateField('kpis', kpis);
  };
  const updateKpi = (idx: number, field: string, value: any) => {
    const kpis = [...(editData.kpis || [])];
    kpis[idx] = { ...kpis[idx], [field]: value };
    updateField('kpis', kpis);
  };
  const removeKpi = (idx: number) => {
    updateField('kpis', (editData.kpis || []).filter((_, i) => i !== idx));
  };

  const data = editing ? editData : strategy;

  const renderAddButton = (label: string, onClick: () => void) => (
    <button className={styles.addItemBtn} onClick={onClick} type="button">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      {label}
    </button>
  );

  const renderRemoveButton = (onClick: () => void) => (
    <button className={styles.removeItemBtn} onClick={onClick} type="button" title="Verwijderen">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );

  return (
    <div className={styles.strategyDetail}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerInfo}>
            {editing ? (
              <>
                <input
                  className={styles.editTitleInput}
                  value={editData.name}
                  onChange={e => updateField('name', e.target.value)}
                  placeholder="Strategienaam"
                />
                <div className={styles.headerMeta}>
                  <select
                    className={styles.editHeaderSelect}
                    value={editData.type}
                    onChange={e => updateField('type', e.target.value)}
                  >
                    {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <select
                    className={styles.editHeaderSelect}
                    value={editData.status}
                    onChange={e => updateField('status', e.target.value)}
                  >
                    {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <h1>{data.name}</h1>
                <div className={styles.headerMeta}>
                  <span className={styles.typeBadge}>{typeLabels[data.type] || data.type}</span>
                  <span className={styles.statusBadge} style={{ backgroundColor: statusColors[data.status] || '#999' }}>
                    {statusLabels[data.status] || data.status}
                  </span>
                </div>
                {data.coreMessage && (
                  <p className={styles.coreMessagePreview}>
                    {data.coreMessage.length > 120 ? data.coreMessage.substring(0, 120) + '...' : data.coreMessage}
                  </p>
                )}
              </>
            )}
          </div>
          <div className={styles.headerActions}>
            {editing ? (
              <>
                <button className={styles.saveBtn} onClick={saveChanges} disabled={saving}>
                  {saving ? 'Opslaan...' : 'Opslaan'}
                </button>
                <button className={styles.backBtn} onClick={cancelEditing}>Annuleren</button>
              </>
            ) : (
              <>
                <button className={styles.editBtn} onClick={startEditing}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Bewerken
                </button>
                <button className={styles.backBtn} onClick={onClose}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Terug
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`} onClick={() => setActiveTab('overview')}>
          Overzicht
        </button>
        <button className={`${styles.tab} ${activeTab === 'stakeholders' ? styles.active : ''}`} onClick={() => setActiveTab('stakeholders')}>
          Stakeholders
        </button>
        <button className={`${styles.tab} ${activeTab === 'issues' ? styles.active : ''}`} onClick={() => setActiveTab('issues')}>
          Issues & Risico's
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'overview' && (
          <div>
            {/* Core Message */}
            <div className={styles.coreMessageBox}>
              <h3>Kernboodschap (Story of the Day)</h3>
              {editing ? (
                <textarea
                  className={styles.editTextarea}
                  value={editData.coreMessage || ''}
                  onChange={e => updateField('coreMessage', e.target.value)}
                  rows={3}
                  placeholder="De kernboodschap van deze strategie..."
                />
              ) : data.coreMessage ? (
                <p>{data.coreMessage}</p>
              ) : (
                <p className={styles.emptyText}>Nog geen kernboodschap ingevuld.</p>
              )}
            </div>

            {/* Description */}
            <div className={styles.section}>
              <h3>Beschrijving</h3>
              {editing ? (
                <textarea
                  className={styles.editTextarea}
                  value={editData.description}
                  onChange={e => updateField('description', e.target.value)}
                  rows={4}
                  placeholder="Beschrijf de strategie..."
                />
              ) : (
                <p className={styles.descriptionText}>{data.description}</p>
              )}
            </div>

            {/* Theoretical Basis */}
            <div className={styles.section}>
              <h3>Theoretisch Kader</h3>
              {editing ? (
                <textarea
                  className={styles.editTextarea}
                  value={editData.theoreticalBasis || ''}
                  onChange={e => updateField('theoreticalBasis', e.target.value)}
                  rows={4}
                  placeholder="Bijv. SOM/MGA-model, IAP2 Spectrum, Arnstein's Ladder..."
                />
              ) : data.theoreticalBasis ? (
                <p className={styles.descriptionText}>{data.theoreticalBasis}</p>
              ) : (
                <p className={styles.emptyText}>Nog geen theoretisch kader ingevuld.</p>
              )}
            </div>

            {/* Objectives */}
            <div className={styles.section}>
              <h3>Doelstellingen</h3>
              {editing ? (
                <div className={styles.editList}>
                  {(editData.objectives || []).map((obj, i) => (
                    <div key={i} className={styles.editListItem}>
                      {renderRemoveButton(() => removeObjective(i))}
                      <div className={styles.editListFields}>
                        <input
                          className={styles.editInput}
                          value={obj.title}
                          onChange={e => updateObjective(i, 'title', e.target.value)}
                          placeholder="Doelstelling"
                        />
                        <input
                          className={styles.editInput}
                          value={obj.description || ''}
                          onChange={e => updateObjective(i, 'description', e.target.value)}
                          placeholder="Toelichting (optioneel)"
                        />
                        <div className={styles.editRow}>
                          <input
                            className={styles.editInput}
                            value={obj.measurable || ''}
                            onChange={e => updateObjective(i, 'measurable', e.target.value)}
                            placeholder="Meetbaar criterium"
                          />
                          <label className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={obj.completed || false}
                              onChange={e => updateObjective(i, 'completed', e.target.checked)}
                            />
                            Afgerond
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  {renderAddButton('Doelstelling toevoegen', addObjective)}
                </div>
              ) : data.objectives && data.objectives.length > 0 ? (
                <div className={styles.objectivesList}>
                  {data.objectives.map((obj, i) => (
                    <div key={i} className={styles.objectiveItem}>
                      <div className={`${styles.objectiveCheck} ${obj.completed ? styles.completed : styles.pending}`}>
                        {obj.completed ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1" /></svg>
                        )}
                      </div>
                      <div className={styles.objectiveContent}>
                        <h4>{obj.title}</h4>
                        {obj.description && <p>{obj.description}</p>}
                        {obj.measurable && <p>Meetbaar: {obj.measurable}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen doelstellingen ingevuld.</p>
              )}
            </div>

            {/* Key Messages */}
            <div className={styles.section}>
              <h3>Kernboodschappen</h3>
              {editing ? (
                <div className={styles.editList}>
                  {(editData.keyMessages || []).map((msg, i) => (
                    <div key={i} className={styles.editListItem}>
                      {renderRemoveButton(() => removeKeyMessage(i))}
                      <div className={styles.editListFields}>
                        <textarea
                          className={styles.editTextarea}
                          value={msg.message}
                          onChange={e => updateKeyMessage(i, 'message', e.target.value)}
                          rows={2}
                          placeholder="Boodschap"
                        />
                        <div className={styles.editRow}>
                          <input
                            className={styles.editInput}
                            value={msg.audience || ''}
                            onChange={e => updateKeyMessage(i, 'audience', e.target.value)}
                            placeholder="Doelgroep"
                          />
                          <input
                            className={styles.editInput}
                            value={msg.channel || ''}
                            onChange={e => updateKeyMessage(i, 'channel', e.target.value)}
                            placeholder="Kanaal"
                          />
                          <select
                            className={styles.editSelect}
                            value={msg.priority || 'medium'}
                            onChange={e => updateKeyMessage(i, 'priority', e.target.value)}
                          >
                            <option value="low">Laag</option>
                            <option value="medium">Gemiddeld</option>
                            <option value="high">Hoog</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                  {renderAddButton('Kernboodschap toevoegen', addKeyMessage)}
                </div>
              ) : data.keyMessages && data.keyMessages.length > 0 ? (
                <div className={styles.messagesList}>
                  {data.keyMessages.map((msg, i) => (
                    <div key={i} className={styles.messageItem}>
                      <p>{msg.message}</p>
                      <div className={styles.messageMeta}>
                        {msg.audience && <span>Doelgroep: {msg.audience}</span>}
                        {msg.channel && <span>Kanaal: {msg.channel}</span>}
                        {msg.priority && <span>Prioriteit: {msg.priority}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen kernboodschappen ingevuld.</p>
              )}
            </div>

            {/* Timeline & Budget */}
            <div className={styles.section}>
              <h3>Planning & Budget</h3>
              {editing ? (
                <div className={styles.editRow}>
                  <div className={styles.editFieldGroup}>
                    <label className={styles.editLabel}>Startdatum</label>
                    <input
                      className={styles.editInput}
                      type="date"
                      value={editData.timeline?.startDate ? new Date(editData.timeline.startDate).toISOString().split('T')[0] : ''}
                      onChange={e => updateField('timeline', { ...editData.timeline, startDate: e.target.value || undefined })}
                    />
                  </div>
                  <div className={styles.editFieldGroup}>
                    <label className={styles.editLabel}>Einddatum</label>
                    <input
                      className={styles.editInput}
                      type="date"
                      value={editData.timeline?.endDate ? new Date(editData.timeline.endDate).toISOString().split('T')[0] : ''}
                      onChange={e => updateField('timeline', { ...editData.timeline, endDate: e.target.value || undefined })}
                    />
                  </div>
                  <div className={styles.editFieldGroup}>
                    <label className={styles.editLabel}>Budget (EUR)</label>
                    <input
                      className={styles.editInput}
                      type="number"
                      value={editData.budget?.total || ''}
                      onChange={e => updateField('budget', { ...editData.budget, total: e.target.value ? parseFloat(e.target.value) : undefined })}
                      placeholder="50000"
                    />
                  </div>
                </div>
              ) : (
                <>
                <div className={styles.infoGrid}>
                  {data.timeline?.startDate && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Startdatum</span>
                      <span className={styles.infoValue}>{new Date(data.timeline.startDate).toLocaleDateString('nl-NL')}</span>
                    </div>
                  )}
                  {data.timeline?.endDate && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Einddatum</span>
                      <span className={styles.infoValue}>{new Date(data.timeline.endDate).toLocaleDateString('nl-NL')}</span>
                    </div>
                  )}
                  {data.budget?.total !== undefined && data.budget.total > 0 && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Budget</span>
                      <span className={styles.infoValue}>{data.budget.currency || 'EUR'} {data.budget.total.toLocaleString('nl-NL')}</span>
                    </div>
                  )}
                  {!data.timeline?.startDate && !data.timeline?.endDate && (!data.budget?.total || data.budget.total === 0) && (
                    <p className={styles.emptyText}>Nog geen planning of budget ingevuld.</p>
                  )}
                </div>
                {/* Budget onderbouwing */}
                {data.budget?.rationale && (
                  <div className={styles.budgetRationale}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#277CEA" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
                    </svg>
                    <span>{data.budget.rationale}</span>
                  </div>
                )}
                {/* Budget breakdown */}
                {data.budget?.breakdown && data.budget.breakdown.length > 0 && (
                  <div className={styles.budgetBreakdown}>
                    <h4>Budgetuitsplitsing</h4>
                    <table className={styles.budgetTable}>
                      <thead>
                        <tr>
                          <th>Categorie</th>
                          <th>Bedrag</th>
                          <th>Toelichting</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.budget.breakdown.map((item, i) => (
                          <tr key={i}>
                            <td>{item.category}</td>
                            <td className={styles.budgetAmount}>{item.amount > 0 ? `€ ${item.amount.toLocaleString('nl-NL')}` : '-'}</td>
                            <td className={styles.budgetDescription}>{item.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                </>
              )}
            </div>

            {/* Channels */}
            <div className={styles.section}>
              <h3>Communicatiekanalen</h3>
              {editing ? (
                <input
                  className={styles.editInput}
                  value={(editData.channels || []).join(', ')}
                  onChange={e => updateField('channels', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder="Website, Nieuwsbrief, Social media, Informatiebijeenkomst"
                />
              ) : data.channels && data.channels.length > 0 ? (
                <div className={styles.tagList}>
                  {data.channels.map((channel, i) => (
                    <span key={i} className={styles.tagItem}>{channel}</span>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen kanalen ingevuld.</p>
              )}
            </div>

            {/* Notes */}
            <div className={styles.section}>
              <h3>Notities</h3>
              {editing ? (
                <textarea
                  className={styles.editTextarea}
                  value={editData.notes || ''}
                  onChange={e => updateField('notes', e.target.value)}
                  rows={3}
                  placeholder="Aanvullende opmerkingen..."
                />
              ) : data.notes ? (
                <p className={styles.descriptionText}>{data.notes}</p>
              ) : (
                <p className={styles.emptyText}>Geen notities.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'stakeholders' && (
          <div>
            {/* Stakeholder Mapping Table */}
            <div className={styles.section}>
              <h3>Stakeholder Mapping (SOM/MGA)</h3>
              {editing ? (
                <div className={styles.editList}>
                  {(editData.stakeholderMapping?.actors || []).map((actor, i) => (
                    <div key={i} className={styles.editListItem}>
                      {renderRemoveButton(() => removeActor(i))}
                      <div className={styles.editListFields}>
                        <div className={styles.editRow}>
                          <input className={styles.editInput} value={actor.name} onChange={e => updateActor(i, 'name', e.target.value)} placeholder="Actor naam" />
                          <input className={styles.editInput} value={actor.category || ''} onChange={e => updateActor(i, 'category', e.target.value)} placeholder="Categorie" />
                        </div>
                        <div className={styles.editRow}>
                          <input className={styles.editInput} value={actor.position || ''} onChange={e => updateActor(i, 'position', e.target.value)} placeholder="Standpunt" />
                          <input className={styles.editInput} value={actor.interest || ''} onChange={e => updateActor(i, 'interest', e.target.value)} placeholder="Belang" />
                          <input className={styles.editInput} value={actor.exchangeSpace || ''} onChange={e => updateActor(i, 'exchangeSpace', e.target.value)} placeholder="Uitruilruimte" />
                        </div>
                      </div>
                    </div>
                  ))}
                  {renderAddButton('Actor toevoegen', addActor)}
                </div>
              ) : data.stakeholderMapping?.actors && data.stakeholderMapping.actors.length > 0 ? (
                <table className={styles.stakeholderTable}>
                  <thead>
                    <tr>
                      <th>Actor</th>
                      <th>Categorie</th>
                      <th>Standpunt</th>
                      <th>Belang</th>
                      <th>Uitruilruimte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stakeholderMapping.actors.map((actor, i) => (
                      <tr key={i}>
                        <td><strong>{actor.name}</strong></td>
                        <td>{actor.category || '-'}</td>
                        <td>{actor.position || '-'}</td>
                        <td>{actor.interest || '-'}</td>
                        <td>{actor.exchangeSpace || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className={styles.emptyText}>Nog geen stakeholder mapping ingevuld.</p>
              )}
            </div>

            {/* Interest Matrix — gestructureerde tabel */}
            <div className={styles.section}>
              <h3>Belangenmatrix</h3>
              {(() => {
                const matrixData = (editing ? editData : data).stakeholderMapping?.interestMatrixData;
                const hasStructured = matrixData && matrixData.length > 0;
                const hasText = (editing ? editData : data).stakeholderMapping?.interestMatrix;

                if (editing) {
                  return (
                    <>
                      {/* Gestructureerde matrix edit */}
                      <table className={styles.stakeholderTable}>
                        <thead>
                          <tr><th>Stakeholder</th><th>Invloed</th><th>Belang</th><th>Positie</th><th></th></tr>
                        </thead>
                        <tbody>
                          {(editData.stakeholderMapping?.interestMatrixData || []).map((item: any, i: number) => (
                            <tr key={i}>
                              <td>
                                <select className={styles.editSelect} value={item.stakeholder || ''}
                                  onChange={e => {
                                    const updated = [...(editData.stakeholderMapping?.interestMatrixData || [])];
                                    updated[i] = { ...updated[i], stakeholder: e.target.value };
                                    updateField('stakeholderMapping', { ...editData.stakeholderMapping, interestMatrixData: updated });
                                  }}>
                                  <option value="">Selecteer...</option>
                                  {(editData.stakeholderMapping?.actors || []).map((a: any) => (
                                    <option key={a.name} value={a.name}>{a.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select className={styles.editSelect} value={item.invloed || 'gemiddeld'}
                                  onChange={e => {
                                    const updated = [...(editData.stakeholderMapping?.interestMatrixData || [])];
                                    updated[i] = { ...updated[i], invloed: e.target.value };
                                    updateField('stakeholderMapping', { ...editData.stakeholderMapping, interestMatrixData: updated });
                                  }}>
                                  <option value="laag">Laag</option>
                                  <option value="gemiddeld">Gemiddeld</option>
                                  <option value="hoog">Hoog</option>
                                </select>
                              </td>
                              <td>
                                <select className={styles.editSelect} value={item.belang || 'gemiddeld'}
                                  onChange={e => {
                                    const updated = [...(editData.stakeholderMapping?.interestMatrixData || [])];
                                    updated[i] = { ...updated[i], belang: e.target.value };
                                    updateField('stakeholderMapping', { ...editData.stakeholderMapping, interestMatrixData: updated });
                                  }}>
                                  <option value="laag">Laag</option>
                                  <option value="gemiddeld">Gemiddeld</option>
                                  <option value="hoog">Hoog</option>
                                </select>
                              </td>
                              <td>
                                <input className={styles.editInput} value={item.positie || ''}
                                  onChange={e => {
                                    const updated = [...(editData.stakeholderMapping?.interestMatrixData || [])];
                                    updated[i] = { ...updated[i], positie: e.target.value };
                                    updateField('stakeholderMapping', { ...editData.stakeholderMapping, interestMatrixData: updated });
                                  }}
                                  placeholder="Bijv. nauw betrekken" />
                              </td>
                              <td>
                                <button className={styles.removeBtn} onClick={() => {
                                  const updated = [...(editData.stakeholderMapping?.interestMatrixData || [])];
                                  updated.splice(i, 1);
                                  updateField('stakeholderMapping', { ...editData.stakeholderMapping, interestMatrixData: updated });
                                }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button className={styles.addBtn} onClick={() => {
                        const updated = [...(editData.stakeholderMapping?.interestMatrixData || []), { stakeholder: '', invloed: 'gemiddeld', belang: 'gemiddeld', positie: '' }];
                        updateField('stakeholderMapping', { ...editData.stakeholderMapping, interestMatrixData: updated });
                      }}>+ Stakeholder toevoegen aan matrix</button>
                    </>
                  );
                }

                // View mode
                if (hasStructured) {
                  const invloedColor: Record<string, string> = { hoog: '#1E3A2F', gemiddeld: '#FF9800', laag: '#9DA6A8' };
                  const belangColor: Record<string, string> = { hoog: '#1E3A2F', gemiddeld: '#FF9800', laag: '#9DA6A8' };
                  return (
                    <table className={styles.stakeholderTable}>
                      <thead>
                        <tr><th>Stakeholder</th><th>Invloed</th><th>Belang</th><th>Positie</th></tr>
                      </thead>
                      <tbody>
                        {matrixData.map((item: any, i: number) => (
                          <tr key={i}>
                            <td><strong>{item.stakeholder}</strong></td>
                            <td><span style={{ color: invloedColor[item.invloed] || '#666', fontWeight: 600 }}>{item.invloed}</span></td>
                            <td><span style={{ color: belangColor[item.belang] || '#666', fontWeight: 600 }}>{item.belang}</span></td>
                            <td>{item.positie || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                }

                // Fallback: tekst
                if (hasText) return <p className={styles.matrixText}>{data.stakeholderMapping?.interestMatrix}</p>;
                return <p className={styles.emptyText}>Nog geen belangenmatrix ingevuld.</p>;
              })()}
            </div>

            {/* Participation Levels */}
            <div className={styles.section}>
              <h3>Participatieniveaus</h3>

              {/* Consistentiecheck: waarschuw als stakeholders niet overeenkomen */}
              {(() => {
                const d = editing ? editData : data;
                const actorNames = (d.stakeholderMapping?.actors || []).map((a: any) => a.name?.toLowerCase().trim()).filter(Boolean);
                const plGroups = (d.participationLevels || []).map((pl: any) => pl.stakeholderGroup?.toLowerCase().trim()).filter(Boolean);
                const missingInPL = actorNames.filter((n: string) => !plGroups.some((g: string) => g.includes(n) || n.includes(g)));
                const extraInPL = plGroups.filter((g: string) => !actorNames.some((n: string) => n.includes(g) || g.includes(n)));

                if (actorNames.length > 0 && (missingInPL.length > 0 || extraInPL.length > 0)) {
                  return (
                    <div className={styles.consistencyWarning}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2" strokeLinecap="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span>
                        Stakeholders in mapping en participatieniveaus komen niet overeen.
                        {missingInPL.length > 0 && <> Ontbreken in participatie: <strong>{(missingInPL as string[]).join(', ')}</strong>.</>}
                        {extraInPL.length > 0 && <> Extra in participatie: <strong>{(extraInPL as string[]).join(', ')}</strong>.</>}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              {editing ? (
                <div className={styles.editList}>
                  {(editData.participationLevels || []).map((pl, i) => {
                    const actorOptions = (editData.stakeholderMapping?.actors || []).map((a: any) => a.name).filter(Boolean);
                    return (
                    <div key={i} className={styles.editListItem}>
                      {renderRemoveButton(() => removeParticipationLevel(i))}
                      <div className={styles.editListFields}>
                        <div className={styles.editRow}>
                          {actorOptions.length > 0 ? (
                            <select
                              className={styles.editSelect}
                              value={pl.stakeholderGroup}
                              onChange={e => updateParticipationLevel(i, 'stakeholderGroup', e.target.value)}
                            >
                              <option value="">Selecteer stakeholder...</option>
                              {actorOptions.map((name: string) => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                              {pl.stakeholderGroup && !actorOptions.includes(pl.stakeholderGroup) && (
                                <option value={pl.stakeholderGroup}>{pl.stakeholderGroup} (niet in mapping)</option>
                              )}
                            </select>
                          ) : (
                            <input
                              className={styles.editInput}
                              value={pl.stakeholderGroup}
                              onChange={e => updateParticipationLevel(i, 'stakeholderGroup', e.target.value)}
                              placeholder="Stakeholdergroep"
                            />
                          )}
                          <select
                            className={styles.editSelect}
                            value={pl.level}
                            onChange={e => updateParticipationLevel(i, 'level', e.target.value)}
                          >
                            {participationLadder.map(l => (
                              <option key={l.level} value={l.level}>
                                {l.level.charAt(0).toUpperCase() + l.level.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <input
                          className={styles.editInput}
                          value={pl.reasoning || ''}
                          onChange={e => updateParticipationLevel(i, 'reasoning', e.target.value)}
                          placeholder="Toelichting (optioneel)"
                        />
                      </div>
                    </div>
                    );
                  })}
                  {renderAddButton('Participatieniveau toevoegen', addParticipationLevel)}
                </div>
              ) : data.participationLevels && data.participationLevels.length > 0 ? (
                <div className={styles.participationLadder}>
                  {data.participationLevels.map((level, i) => (
                    <div key={i} className={styles.participationItem}>
                      <span className={styles.participationGroup}>{level.stakeholderGroup}</span>
                      <span className={styles.levelBadge} style={{ backgroundColor: levelColors[level.level] || '#999' }}>
                        {level.level}
                      </span>
                      {level.reasoning && <span className={styles.participationReasoning}>{level.reasoning}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen participatieniveaus ingevuld.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'issues' && (
          <div>
            {/* Issue Analysis */}
            <div className={styles.section}>
              <h3>Issue-analyse</h3>
              {editing ? (
                <div className={styles.issueGrid}>
                  <div className={styles.issueBox}>
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F44336" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span style={{ color: '#F44336' }}>Conflictpunten</span>
                    </h4>
                    {(editData.issueAnalysis?.conflictPoints || []).map((point, i) => (
                      <div key={i} className={styles.editInlineRow}>
                        <input
                          className={styles.editInput}
                          value={point}
                          onChange={e => updateConflictPoint(i, e.target.value)}
                          placeholder="Conflictpunt"
                        />
                        {renderRemoveButton(() => removeConflictPoint(i))}
                      </div>
                    ))}
                    {renderAddButton('Conflictpunt', addConflictPoint)}
                  </div>
                  <div className={styles.issueBox}>
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span style={{ color: '#4CAF50' }}>Kansen / Gains</span>
                    </h4>
                    {(editData.issueAnalysis?.gains || []).map((gain, i) => (
                      <div key={i} className={styles.editInlineRow}>
                        <input
                          className={styles.editInput}
                          value={gain}
                          onChange={e => updateGain(i, e.target.value)}
                          placeholder="Kans"
                        />
                        {renderRemoveButton(() => removeGain(i))}
                      </div>
                    ))}
                    {renderAddButton('Kans toevoegen', addGain)}
                  </div>
                </div>
              ) : (data.issueAnalysis?.conflictPoints?.length || data.issueAnalysis?.gains?.length) ? (
                <div className={styles.issueGrid}>
                  {data.issueAnalysis?.conflictPoints && data.issueAnalysis.conflictPoints.length > 0 && (
                    <div className={styles.issueBox}>
                      <h4>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F44336" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span style={{ color: '#F44336' }}>Conflictpunten</span>
                      </h4>
                      <ul className={styles.issueList}>
                        {data.issueAnalysis.conflictPoints.map((point, i) => (
                          <li key={i} className={styles.issueListItem}><span style={{ color: '#F44336' }}>&#8226;</span>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.issueAnalysis?.gains && data.issueAnalysis.gains.length > 0 && (
                    <div className={styles.issueBox}>
                      <h4>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <span style={{ color: '#4CAF50' }}>Kansen / Gains</span>
                      </h4>
                      <ul className={styles.issueList}>
                        {data.issueAnalysis.gains.map((gain, i) => (
                          <li key={i} className={styles.issueListItem}><span style={{ color: '#4CAF50' }}>&#8226;</span>{gain}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen issue-analyse ingevuld.</p>
              )}
            </div>

            {/* Risks */}
            <div className={styles.section}>
              <h3>Risico's</h3>
              {editing ? (
                <div className={styles.editList}>
                  {(editData.risks || []).map((risk, i) => (
                    <div key={i} className={styles.editListItem}>
                      {renderRemoveButton(() => removeRisk(i))}
                      <div className={styles.editListFields}>
                        <input
                          className={styles.editInput}
                          value={risk.description}
                          onChange={e => updateRisk(i, 'description', e.target.value)}
                          placeholder="Risico beschrijving"
                        />
                        <div className={styles.editRow}>
                          <select className={styles.editSelect} value={risk.impact || 'medium'} onChange={e => updateRisk(i, 'impact', e.target.value)}>
                            <option value="low">Laag impact</option>
                            <option value="medium">Gemiddeld impact</option>
                            <option value="high">Hoog impact</option>
                            <option value="critical">Kritiek</option>
                          </select>
                          <select className={styles.editSelect} value={risk.probability || 'medium'} onChange={e => updateRisk(i, 'probability', e.target.value)}>
                            <option value="low">Lage kans</option>
                            <option value="medium">Gemiddelde kans</option>
                            <option value="high">Hoge kans</option>
                          </select>
                        </div>
                        <input
                          className={styles.editInput}
                          value={risk.mitigation || ''}
                          onChange={e => updateRisk(i, 'mitigation', e.target.value)}
                          placeholder="Mitigatie strategie"
                        />
                      </div>
                    </div>
                  ))}
                  {renderAddButton('Risico toevoegen', addRisk)}
                </div>
              ) : data.risks && data.risks.length > 0 ? (
                <div className={styles.riskGrid}>
                  {data.risks.map((risk, i) => {
                    const colors = riskColors[risk.impact || 'medium'] || riskColors.medium;
                    return (
                      <div key={i} className={styles.riskCard} style={{ borderLeftColor: colors.border }}>
                        <h4>{risk.description}</h4>
                        <div className={styles.riskMeta}>
                          {risk.impact && <span className={styles.riskBadge} style={{ background: colors.bg, color: colors.text }}>Impact: {risk.impact}</span>}
                          {risk.probability && <span className={styles.riskBadge} style={{ background: '#F0F0F0', color: '#666' }}>Kans: {risk.probability}</span>}
                        </div>
                        {risk.mitigation && <p className={styles.riskMitigation}><strong>Mitigatie:</strong> {risk.mitigation}</p>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen risico's ingevuld.</p>
              )}
            </div>

            {/* KPIs */}
            <div className={styles.section}>
              <h3>KPI's</h3>
              {editing ? (
                <div className={styles.editList}>
                  {(editData.kpis || []).map((kpi, i) => (
                    <div key={i} className={styles.editListItem}>
                      {renderRemoveButton(() => removeKpi(i))}
                      <div className={styles.editListFields}>
                        <div className={styles.editRow}>
                          <input className={styles.editInput} value={kpi.name} onChange={e => updateKpi(i, 'name', e.target.value)} placeholder="KPI naam" />
                          <input className={styles.editInput} value={kpi.unit || ''} onChange={e => updateKpi(i, 'unit', e.target.value)} placeholder="Eenheid (bijv. %)" />
                        </div>
                        <input className={styles.editInput} value={kpi.description || ''} onChange={e => updateKpi(i, 'description', e.target.value)} placeholder="Beschrijving" />
                        <div className={styles.editRow}>
                          <div className={styles.editFieldGroup}>
                            <label className={styles.editLabel}>Doel</label>
                            <input className={styles.editInput} type="number" value={kpi.target || ''} onChange={e => updateKpi(i, 'target', e.target.value ? parseFloat(e.target.value) : undefined)} />
                          </div>
                          <div className={styles.editFieldGroup}>
                            <label className={styles.editLabel}>Huidig</label>
                            <input className={styles.editInput} type="number" value={kpi.current || ''} onChange={e => updateKpi(i, 'current', e.target.value ? parseFloat(e.target.value) : undefined)} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {renderAddButton('KPI toevoegen', addKpi)}
                </div>
              ) : data.kpis && data.kpis.length > 0 ? (
                <div className={styles.infoGrid}>
                  {data.kpis.map((kpi, i) => (
                    <div key={i} className={styles.infoItem}>
                      <span className={styles.infoLabel}>{kpi.name}</span>
                      <span className={styles.infoValue}>
                        {kpi.current !== undefined ? `${kpi.current} / ${kpi.target}` : `Doel: ${kpi.target}`}
                        {kpi.unit && ` ${kpi.unit}`}
                      </span>
                      {kpi.description && <span style={{ color: '#666', fontSize: '0.85rem' }}>{kpi.description}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>Nog geen KPI's ingevuld.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
